/**
 * Compliance → Restriction - Phase 8 batch D item 1 (2026-05-17).
 *
 * GDPR Art 18 processing-restriction executor. Third in the subject-rights
 * trio (after Erasure and Rectification). Mirrors the same KPI bento +
 * submit form + queue + detail modal pattern, with one extra terminal
 * action ("Lift restriction") on rows in the `restricted` state.
 *
 * Differences from Erasure/Rectification:
 *   • A required `restrictionBasis` (Art 18(1) enum) on submit.
 *   • Optional `untilDate` sunset (datetime-local input).
 *   • `restricted` is a NORMAL terminal status (not an error) - the data
 *     is now under processing-restriction. The `lifted` action returns
 *     it to normal processing.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Plus,
  ShieldCheck,
  Play,
  Eye,
  Ban,
  Pause,
  Unlock,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listRestrictionJobs,
  createRestrictionRequest,
  approveRestrictionJob,
  executeRestrictionJob,
  liftRestrictionJob,
  type RestrictionJob,
  type RestrictionJobStatus,
  type RestrictionIdentifierKind,
  type RestrictionBasis,
} from '@/lib/api';

const STATUS_VARIANT: Record<RestrictionJobStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  pending: 'glass',
  approved: 'warning',
  running: 'warning',
  restricted: 'warning',
  lifted: 'success',
  failed: 'critical',
  cancelled: 'critical',
};

const STATUS_ICON: Record<RestrictionJobStatus, typeof CheckCircle2> = {
  pending: Clock,
  approved: ShieldCheck,
  running: Loader2,
  restricted: Pause,
  lifted: Unlock,
  failed: XCircle,
  cancelled: Ban,
};

const IDENTIFIER_OPTIONS: ReadonlyArray<{ value: RestrictionIdentifierKind; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'pan', label: 'PAN' },
  { value: 'customer_number', label: 'Customer number' },
  { value: 'custom', label: 'Custom identifier' },
];

const BASIS_OPTIONS: ReadonlyArray<{ value: RestrictionBasis; label: string; hint: string }> = [
  { value: 'accuracy_challenged', label: 'Accuracy challenged (Art 18(1)(a))', hint: 'Subject disputes accuracy; restrict until verified.' },
  { value: 'unlawful_subject_opposes', label: 'Unlawful but subject opposes erasure (Art 18(1)(b))', hint: 'Processing is unlawful but the subject wants the data kept for legal claims.' },
  { value: 'controller_no_longer_needs', label: 'Controller no longer needs, subject does (Art 18(1)(c))', hint: 'We would normally delete; subject asked us to keep for their legal claims.' },
  { value: 'objection_pending', label: 'Objection pending (Art 18(1)(d))', hint: 'Art 21 objection under review - pause processing in the interim.' },
];

export function ComplianceRestriction(): JSX.Element {
  const [jobs, setJobs] = useState<RestrictionJob[]>([]);
  const [counts, setCounts] = useState({
    pending: 0, approved: 0, running: 0, restricted: 0, lifted: 0, failed: 0, cancelled: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [identifierKind, setIdentifierKind] = useState<RestrictionIdentifierKind>('email');
  const [principalIdentifier, setPrincipalIdentifier] = useState('');
  const [restrictionBasis, setRestrictionBasis] = useState<RestrictionBasis>('accuracy_challenged');
  const [untilDate, setUntilDate] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [detailJob, setDetailJob] = useState<RestrictionJob | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listRestrictionJobs({});
      setJobs(r.jobs);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load restriction jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleSubmit(): Promise<void> {
    if (!principalIdentifier.trim()) {
      setError('Principal identifier is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // datetime-local emits a string in the browser's local zone WITHOUT
      // an offset, e.g. "2026-06-01T14:30". Append :00Z to convert to UTC
      // ISO-8601 - naive but correct enough for a demo control plane and
      // avoids the awkward "user pasted a date but it parses as midnight
      // local" surprise. Production replaces with a proper datetime picker.
      const untilIso = untilDate.trim() ? new Date(untilDate).toISOString() : undefined;
      await createRestrictionRequest({
        principalIdentifier: principalIdentifier.trim(),
        identifierKind,
        restrictionBasis,
        ...(untilIso ? { untilDate: untilIso } : {}),
        ...(requestReason.trim() ? { requestReason: requestReason.trim() } : {}),
      });
      setPrincipalIdentifier('');
      setRequestReason('');
      setUntilDate('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to submit restriction request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApprove(j: RestrictionJob): Promise<void> {
    const reason = window.prompt(`Approve restriction for principal ${j.principalIdentifierHash.slice(0, 12)}…?\nApproval reason:`);
    if (!reason) return;
    try {
      await approveRestrictionJob(j.id, reason);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'approve failed');
    }
  }

  async function handleExecute(j: RestrictionJob): Promise<void> {
    if (!window.confirm(
      `Apply processing-restriction across ${j.assetsScope.length} asset(s)? ` +
      `Detection-side firewall will short-circuit queries hitting this principal until lifted.`,
    )) return;
    try {
      await executeRestrictionJob(j.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'execute failed');
    }
  }

  async function handleLift(j: RestrictionJob): Promise<void> {
    const reason = window.prompt(
      `Lift restriction for ${j.principalIdentifierHash.slice(0, 12)}…?\n` +
      `Data returns to normal processing. Reason:`,
    );
    if (!reason) return;
    try {
      await liftRestrictionJob(j.id, reason);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'lift failed');
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Right to restriction (GDPR Art 18)"
        subtitle="Pause processing of a subject's data without deleting or modifying it. Detection-side firewall short-circuits queries hitting restricted principals until the DPO lifts. The Art 18(1) basis is recorded for the regulator audit."
        badges={[
          { label: 'DPO-gated', variant: 'glass' },
          { label: 'Art 18(1) basis logged', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Restriction action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Pending" value={counts.pending} icon={Clock} glow={counts.pending > 0 ? 'medium' : 'low'} hint="awaiting approval" />
        <KpiCard label="Approved" value={counts.approved} icon={ShieldCheck} glow={counts.approved > 0 ? 'medium' : 'low'} hint="ready to apply" />
        <KpiCard label="Restricted" value={counts.restricted} icon={Pause} glow={counts.restricted > 0 ? 'high' : 'low'} hint="active firewall" />
        <KpiCard label="Lifted" value={counts.lifted} icon={Unlock} glow="low" hint="returned to normal" />
        <KpiCard label="Failed" value={counts.failed} icon={XCircle} glow={counts.failed > 0 ? 'critical' : 'low'} hint="see execution log" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Submit restriction request
            </CardTitle>
            <CardDescription>
              Identifier is SHA-256 hashed. The Art 18(1) basis is required and recorded for the
              regulator audit. The optional sunset auto-lifts restriction past that date.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-muted-foreground block mb-0.5">Identifier kind</label>
              <select
                value={identifierKind}
                onChange={(e) => setIdentifierKind(e.target.value as RestrictionIdentifierKind)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {IDENTIFIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Principal identifier</label>
              <input
                type="text"
                value={principalIdentifier}
                onChange={(e) => setPrincipalIdentifier(e.target.value)}
                placeholder="value"
                maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Art 18(1) basis</label>
              <select
                value={restrictionBasis}
                onChange={(e) => setRestrictionBasis(e.target.value as RestrictionBasis)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {BASIS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {BASIS_OPTIONS.find((b) => b.value === restrictionBasis)?.hint}
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Sunset (optional)</label>
              <input
                type="datetime-local"
                value={untilDate}
                onChange={(e) => setUntilDate(e.target.value)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Leave empty for indefinite restriction. Future date only.
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Reason (optional)</label>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Subject contested accuracy on phone field via webform"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_restriction.a01" onClick={() => void handleSubmit()} disabled={submitting}>
                {submitting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Pause className="h-3.5 w-3.5 mr-1" />}
                Submit request
              </Button>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Restriction queue</CardTitle>
            <CardDescription>
              <strong>pending → approved → running → restricted → lifted</strong>. Each transition
              is audit-chain logged. Active restrictions feed the Detection-side firewall.
            </CardDescription>
          </CardHeader>
          {loading && (
            <LoadingState caption="Loading…" />
          )}
          {!loading && jobs.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Pause className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <div>No restriction jobs yet.</div>
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div className="space-y-2">
              {jobs.map((j, i) => {
                const SIcon = STATUS_ICON[j.status];
                return (
                  <motion.div
                    key={j.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="glass-lite rounded p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="glass" className="text-[10px] font-mono">{j.identifierKind}</Badge>
                          <Badge variant={STATUS_VARIANT[j.status]} className="text-[10px] inline-flex items-center gap-1">
                            <SIcon className={`h-3 w-3 ${j.status === 'running' ? 'animate-spin' : ''}`} />
                            {j.status}
                          </Badge>
                          <Badge variant="glass" className="text-[10px]">{j.restrictionBasis.replace(/_/g, ' ')}</Badge>
                          <span className="text-[10px] text-muted-foreground">{j.assetsScope.length} asset(s)</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" title={j.principalIdentifierHash}>
                          {j.principalIdentifierHash.slice(0, 24)}…
                        </div>
                        {j.untilDate && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">
                            sunset: <code className="font-mono">{new Date(j.untilDate).toLocaleString()}</code>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" variant="glass" onClick={() => setDetailJob(j)} title="Details">
                          <Eye className="h-3 w-3" />
                        </Button>
                        {j.status === 'pending' && (
                          <Button size="sm" variant="glass" data-action="disc.comp_restriction.a02" onClick={() => void handleApprove(j)} title="Approve">
                            <ShieldCheck className="h-3 w-3" />
                          </Button>
                        )}
                        {j.status === 'approved' && (
                          <Button size="sm" variant="glass" data-action="disc.comp_restriction.a03" onClick={() => void handleExecute(j)} title="Apply">
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                        {j.status === 'restricted' && (
                          <Button size="sm" variant="glass" data-action="disc.comp_restriction.a04" onClick={() => void handleLift(j)} title="Lift restriction">
                            <Unlock className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </div>

      <Dialog open={detailJob !== null} onOpenChange={(o) => { if (!o) setDetailJob(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Restriction job detail</DialogTitle>
          </DialogHeader>
          {detailJob && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Status</div>
                  <Badge variant={STATUS_VARIANT[detailJob.status]} className="text-[10px]">{detailJob.status}</Badge>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Art 18(1) basis</div>
                  <code className="font-mono">{detailJob.restrictionBasis}</code>
                </div>
                <div className="glass-lite rounded p-2 col-span-2">
                  <div className="text-muted-foreground mb-0.5">Principal hash</div>
                  <code className="font-mono text-[10px] break-all">{detailJob.principalIdentifierHash}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Sunset</div>
                  <code className="font-mono">{detailJob.untilDate ? new Date(detailJob.untilDate).toLocaleString() : 'indefinite'}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Applied at</div>
                  <code className="font-mono">{detailJob.completedAt ? new Date(detailJob.completedAt).toLocaleString() : '-'}</code>
                </div>
              </div>

              {detailJob.approverActorId && (
                <div className="glass-lite rounded p-3">
                  <div className="font-medium mb-1">Approval</div>
                  <div className="text-muted-foreground">
                    By <code className="font-mono">{detailJob.approverActorId.slice(0, 8)}…</code>
                    {detailJob.approverDecidedAt && <> at {new Date(detailJob.approverDecidedAt).toLocaleString()}</>}
                  </div>
                  {detailJob.approverReason && (
                    <div className="mt-1">Reason: <em>{detailJob.approverReason}</em></div>
                  )}
                </div>
              )}

              {detailJob.liftedAt && (
                <div className="glass-lite rounded p-3">
                  <div className="font-medium mb-1">Lifted</div>
                  <div className="text-muted-foreground">
                    By <code className="font-mono">{detailJob.liftedByActorId?.slice(0, 8) ?? '?'}…</code> at{' '}
                    {new Date(detailJob.liftedAt).toLocaleString()}
                  </div>
                  {detailJob.liftReason && (
                    <div className="mt-1">Reason: <em>{detailJob.liftReason}</em></div>
                  )}
                </div>
              )}

              <div>
                <div className="font-medium mb-1">Assets in scope ({detailJob.assetsScope.length})</div>
                {detailJob.assetsScope.length === 0 ? (
                  <div className="glass-lite rounded p-3 text-muted-foreground italic">
                    The access-graph walk found no assets touching this principal.
                  </div>
                ) : (
                  <div className="glass-lite rounded p-2 max-h-32 overflow-y-auto">
                    {detailJob.assetsScope.map((id) => (
                      <code key={id} className="block font-mono text-[10px]">{id}</code>
                    ))}
                  </div>
                )}
              </div>

              {detailJob.executionLog.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Execution log</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detailJob.executionLog.map((e, i) => (
                      <div key={i} className="glass-lite rounded p-2 flex items-start gap-2">
                        <Badge
                          variant={e.status === 'ok' ? 'success' : e.status === 'failed' ? 'critical' : 'warning'}
                          className="text-[9px] shrink-0"
                        >
                          {e.status}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[10px] text-muted-foreground">{e.assetId}</div>
                          <div>{e.detail}</div>
                          <div className="text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString()} · {e.action}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailJob.errorDetail && (
                <div className="glass-lite border border-sev-critical/40 rounded p-2 text-sev-critical">
                  <div className="font-medium">Error</div>
                  <div>{detailJob.errorDetail}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
